import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CartRoutes } from '@core/constants/routes.const';
import { PhExceptionErrorType } from '@core/enums/ph-exception-error-type.enum';
import { CheckoutService } from '@core/services/checkout.service';
import { GtmService } from '@core/services/gtm.service';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { getErrorMessage } from '@shared/utils/get-error-message';
import { getErrorTitle } from '@shared/utils/get-error-title';
import { groupPurchaseLimmitRules } from '@shared/utils/purchase-limit-rule-utils';
import { ToastrService } from 'ngx-toastr';
import { of } from 'rxjs';
import { catchError, exhaustMap, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { AppState } from '..';
import { logOutResetConcluded } from '../auth/auth.actions';
import { selectCart } from '../cart';
import { setMultiplePurchaseLimitRuleItems, setPurchaseLimitRuleModal } from '../cart/cart.actions';
import { selectAssignedConsultant, selectCurrentConsultant } from '../consultant';
import {
  fetchAssignedConsultantOnCheckout,
  fetchCurrentConsultantByVanityName,
  setIsOutdatedData,
} from '../consultant/consultant.actions';
import * as checkoutActions from './checkout.actions';

@Injectable()
export class CheckoutEffects {
  updatePersonalInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(checkoutActions.updatePersonalInfo),
      mergeMap((address) =>
        this.checkoutService
          .addressValidation({
            skipAddressVerification: address.skipAddressVerification,
            shippingAddress: {
              firstName: address.personalInfo.shippingAddress.firstName,
              lastName: address.personalInfo.shippingAddress.lastName,
              phoneNumber: address.personalInfo.shippingAddress.phoneNumber,
              ...address.personalInfo.shippingAddress.address,
            },
            billingCustomer: address.personalInfo.billingCustomer,
          })
          .pipe(
            map((res) => {
              if (!res.saveSuccessful) {
                return checkoutActions.checkoutAddressValidation({ addressValidation: res });
              }
              return checkoutActions.updatePersonalInfoSuccess({
                personalInfo: address.personalInfo,
              });
            }),
            catchError((error) => of(checkoutActions.updatePersonalInfoFailure(error))),
          ),
      ),
    ),
  );

  updatePersonalInfoSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(checkoutActions.updatePersonalInfoSuccess),
      concatLatestFrom(() => [
        this.store$.select(selectCart),
        this.store$.select(selectCurrentConsultant),
        this.store$.select(selectAssignedConsultant),
      ]),
      tap(([, cart]) => this.gtmService.addShippingInfo(cart)),
      map(([, , currentConsultant, assignedConsultant]) => {
        if (
          currentConsultant?.beeNumber === assignedConsultant?.beeNumber &&
          currentConsultant &&
          assignedConsultant
        ) {
          return fetchAssignedConsultantOnCheckout();
        } else if (currentConsultant) {
          return fetchCurrentConsultantByVanityName({
            vanityName: currentConsultant.vanityName,
            filterCountry: true,
          });
        } else {
          return setIsOutdatedData({ isOutdatedData: false });
        }
      }),
    ),
  );

  updatePersonalInfoFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(checkoutActions.updatePersonalInfoFailure),
        tap(({ error }) => {
          if (error.ErrorType !== PhExceptionErrorType.ForbiddenZipCodeException) {
            this.toastr.error(getErrorMessage(error), getErrorTitle(error));
          }
        }),
      ),
    { dispatch: false },
  );

  checkoutAddressValidation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(checkoutActions.checkoutAddressValidation),
      filter(
        ({ addressValidation }) =>
          !addressValidation?.shippingAddressVerificationResult?.correctedAddress &&
          addressValidation?.shippingAddressVerificationResult?.isValid === false,
      ),
      map(() => checkoutActions.cancelPersonalInfoUpdate()),
    ),
  );

  checkEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(checkoutActions.checkUserByEmail),
      switchMap(({ email }) =>
        this.checkoutService.checkEmail(email).pipe(
          map(() => checkoutActions.checkUserByEmailSuccess()),
          catchError((error) => of(checkoutActions.checkUserByEmailFailure(error))),
        ),
      ),
    ),
  );

  resetCheckoutStore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(checkoutActions.resetCheckoutState),
      map(() => logOutResetConcluded({ state: 'checkoutState' })),
    ),
  );

  checkUserPrincipal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(checkoutActions.checkUserByUserPrincipal),
      switchMap(({ userPrincipal }) =>
        this.checkoutService.checkUserPrincipal(userPrincipal).pipe(
          map(() => checkoutActions.checkUserByPrincipalNameSuccess()),
          catchError((error) => of(checkoutActions.checkUserByPrincipalNameFailure(error))),
        ),
      ),
    ),
  );

  purchaseLimitValidation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(checkoutActions.purchaseLimitValidation),
      exhaustMap(() =>
        this.checkoutService.purchaseLimitValidation().pipe(
          map((response) =>
            checkoutActions.purchaseLimitValidationSuccess({
              payload: response,
            }),
          ),
          catchError((error) => of(checkoutActions.purchaseLimitValidationFailure({ error }))),
        ),
      ),
    ),
  );

  purchaseLimitValidationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(checkoutActions.purchaseLimitValidationSuccess),
      filter(({ payload }) => payload.overLimitItems.length > 0),
      mergeMap(({ payload }) => {
        const groupedItems = groupPurchaseLimmitRules(payload.overLimitItems);
        if (payload.overLimitItems.length > 1) {
          return of(
            setMultiplePurchaseLimitRuleItems({
              payload: {
                items: groupedItems,
                isCartEmpty: payload.isCartEmpty,
              },
            }),
          );
        }
        return of(
          setPurchaseLimitRuleModal({
            payload: payload.overLimitItems[0],
            product: null,
            isCartEmpty: payload.isCartEmpty,
          }),
        );
      }),
    ),
  );

  purchaseLimitValidationSuccessNavigate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(checkoutActions.purchaseLimitValidationSuccess),
        filter(({ payload }) => payload.overLimitItems.length === 0),
        tap(() => this.router.navigate([`/${CartRoutes.Checkout}`])),
      ),
    { dispatch: false },
  );

  purchaseLimitValidationFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(checkoutActions.purchaseLimitValidationFailure),
        tap(({ error }) => {
          this.toastr.error(getErrorMessage(error), getErrorTitle(error));
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private gtmService: GtmService,
    private toastr: ToastrService,
    private checkoutService: CheckoutService,
    private router: Router,
  ) {}
}
